import { Mark, MarkType, Node, Schema } from 'prosemirror-model';
import { Command, EditorState, SelectionRange } from 'prosemirror-state';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';

import { MarkNotFoundError, markTypeErrorReported } from './consts';
import { selectionMenuKey } from './getSelectionPlugins';
import { MarkTypesEnum } from './types';

export const hideSelectionMenu: Command = (state, dispatch) => {
  const isSelectionMenuOpen = selectionMenuKey.getState(state)?.open;
  if (dispatch && isSelectionMenuOpen) {
    dispatch(state.tr.setMeta(selectionMenuKey, { open: false }));

    return true;
  }

  return false;
};

// @see https://github.com/ProseMirror/prosemirror-commands/blob/master/src/commands.js#L467
// Copy of markApplies from the 'prosemirror-commands' as they don't export it.
const markApplies = (doc: Node, ranges: readonly SelectionRange[], type: MarkType) => {
  for (let i = 0; i < ranges.length; i++) {
    let { $from, $to } = ranges[i];
    let can = $from.depth === 0 ? doc.type.allowsMarkType(type) : false;
    // @ts-ignore
    doc.nodesBetween($from.pos, $to.pos, (node: Node) => {
      if (can) {
        return false;
      }
      can = node.inlineContent && node.type.allowsMarkType(type);
    });
    if (can) {
      return true;
    }
  }

  return false;
};

const getSelectionRangeWithoutSpace = (
  $from: SelectionRange['$from'],
  $to: SelectionRange['$to'],
) => {
  let from = $from.pos,
    to = $to.pos,
    start = $from.nodeAfter,
    end = $to.nodeBefore;
  // @ts-ignore
  let spaceStart = start && start.isText ? /^\s*/.exec(start.text)[0].length : 0;
  // @ts-ignore
  let spaceEnd = end && end.isText ? /\s*$/.exec(end.text)[0].length : 0;
  if (from + spaceStart < to) {
    from += spaceStart;
    to -= spaceEnd;
  }

  return { from, to };
};

export const applyMark = (
  $from: SelectionRange['$from'],
  $to: SelectionRange['$to'],
  tr: EditorState['tr'],
  markType: MarkType,
  attrs?: Object,
) => {
  const { from, to } = getSelectionRangeWithoutSpace($from, $to);
  tr.addMark(from, to, markType.create(attrs));
};

// @see https://github.com/ProseMirror/prosemirror-commands/blob/master/src/commands.js#L488
// This function is partial copy of toggleMark from the 'prosemirror-commands'.
// The only change is that we want to check if the particular mark is applied throughout the
// selection, not only the subpart like state.doc.rangeHasMark. So we are using our own
// rangeHasMark to detect the applied mark and toggle accordingly.
export const toggleMark = (markType: MarkType, attrs?: Object, removeAndApply?: boolean) => {
  return (state: EditorState, dispatch: any) => {
    //@ts-ignore
    let { empty, $cursor, ranges } = state.selection;

    if ((empty && !$cursor) || !markApplies(state.doc, ranges, markType)) {
      return false;
    }

    if (dispatch) {
      if ($cursor) {
        if (markType.isInSet(state.storedMarks || $cursor.marks())) {
          dispatch(state.tr.removeStoredMark(markType));
        } else {
          dispatch(state.tr.addStoredMark(markType.create(attrs)));
        }
      } else {
        let has = true,
          tr = state.tr;
        for (let i = 0; has && i < ranges.length; i++) {
          let { $from, $to } = ranges[i];
          has = rangeHasMark($from.pos, $to.pos, markType, state.doc);
        }
        for (let i = 0; i < ranges.length; i++) {
          let { $from, $to } = ranges[i];
          if (has && removeAndApply) {
            tr.removeMark($from.pos, $to.pos, markType);
            applyMark($from, $to, tr, markType, attrs);
          } else if (has) {
            tr.removeMark($from.pos, $to.pos, markType);
          } else {
            applyMark($from, $to, tr, markType, attrs);
          }
        }
        dispatch(tr.scrollIntoView());
      }
    }

    return true;
  };
};

// This function is used to check if the particular mark is applied throughout the selection,
// not only the subpart like state.doc.rangeHasMark.
const rangeHasMark = (from: number, to: number, type: MarkType, doc: Node) => {
  let has = true;
  for (let i = from; has && i < to; i++) {
    has = doc.rangeHasMark(i, i + 1, type);
  }

  return has;
};

function getMarksAttrsBetween(from: number, to: number, state: EditorState) {
  let marks: { from: number; to: number; mark: Mark }[] = [];

  state.doc.nodesBetween(from, to, (node: Node, pos: number) => {
    marks = [
      ...marks,
      ...node.marks
        .filter((mark) => Object.keys(mark.attrs).length)
        .map((mark: Mark) => {
          return {
            from: pos,
            to: pos + node.nodeSize,
            mark,
          };
        }),
    ];
  });

  if (marks.length < 1) {
    return null;
  }

  const firstMark = marks[0];
  const lastMark = marks[marks.length - 1];

  if (firstMark.from > from || lastMark.to < to) {
    return null;
  }

  return firstMark.mark.attrs;
}

export const getMarksAttrs = (markName: MarkTypesEnum, state: EditorState) => {
  let attrs = null;
  if (state && markName) {
    let { from, to } = state.selection;
    attrs = getMarksAttrsBetween(from, to, state);

    return attrs;
  }

  return null;
};

export const getMarkType = (markName: string, schema: Schema) => {
  const type = schema.marks[markName];
  if (!type && !markTypeErrorReported[markName]) {
    markTypeErrorReported[markName] = true;
    ErrorReporter.error(new MarkNotFoundError(markName));
    return null;
  }
  return type;
};

export const isMarkActive = (markName: string, state: EditorState) => {
  if (state && markName) {
    const type = getMarkType(markName, state.schema);
    if (!type) {
      return false;
    }
    let { $from, empty, ranges } = state.selection;
    if (empty) {
      return type.isInSet(state.storedMarks || $from.marks());
    } else {
      let has = true;
      for (let i = 0; has && i < ranges.length; i++) {
        let { $to } = ranges[i];
        has = rangeHasMark($from.pos, $to.pos, type, state.doc);
      }

      return has;
    }
  } else {
    return false;
  }
};
